import { API_BASE, withProfile } from "../api/base";

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const chunkSize = 0x8000;
  let binary = "";

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

async function uploadToBlobViaSas(uploadUrl: string, audioBlob: Blob): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": audioBlob.type || "audio/webm",
    },
    body: audioBlob,
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`SAS upload failed: ${res.status} ${txt}`);
  }
}

async function requestUploadSas(sessionId: string): Promise<{
  blobName: string;
  uploadUrl: string;
  blobUrl: string; // stable (no SAS)
  readUrl: string; // short-lived SAS for immediate playback if desired
}> {
  const r = await fetch(withProfile(`${API_BASE}/api/sessions/${sessionId}/recordings`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "getUploadSas" }),
  });

  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`Failed to get upload SAS: ${r.status} ${txt}`);
  }

  return await r.json();
}

export async function uploadSessionRecording(
  sessionId: string,
  audioBlob: Blob,
  metadata?: Record<string, unknown>
): Promise<string | null> {
  try {
    // Preferred: upload directly to Blob using a short-lived SAS URL (avoids Functions body corruption).
    // Fallback: upload via Functions as base64 ASCII if direct upload fails (e.g., storage CORS not configured yet).
    let stableBlobUrl: string | null = null;
    try {
      const sas = await requestUploadSas(sessionId);
      await uploadToBlobViaSas(sas.uploadUrl, audioBlob);
      stableBlobUrl = sas.blobUrl;
    } catch (e) {
      console.warn("Direct SAS upload failed; falling back to API upload", e);

      // Fallback: send base64 ASCII string body. The recordings function will decode base64 back to bytes.
      const base64Body = await blobToBase64(audioBlob);

      const response = await fetch(
        withProfile(`${API_BASE}/api/sessions/${sessionId}/recordings`),
        {
          method: "POST",
          headers: {
            "Content-Type": audioBlob.type || "audio/webm",
          },
          body: base64Body,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload failed with status:", response.status, errorText);
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result: { id?: string | number; blob_url?: string } =
        await response.json();

      stableBlobUrl = result.blob_url || null;

      if (!stableBlobUrl) {
        throw new Error("Upload succeeded but no blob_url was returned");
      }
    }

    if (!stableBlobUrl) throw new Error("No blob URL available after upload");

    // Create recording metadata row (server will return fresh read SAS URLs on GET).
    if (metadata) {
      const metadataResponse = await fetch(
        withProfile(`${API_BASE}/api/sessions/${sessionId}/recordings`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            blobUrl: stableBlobUrl,
            ...metadata,
          }),
        }
      );

      if (!metadataResponse.ok) {
        const errorText = await metadataResponse.text().catch(() => "");
        throw new Error(
          `Failed to save recording metadata: ${metadataResponse.status} ${errorText}`
        );
      }
    }

    return stableBlobUrl;
  } catch (error) {
    console.error("Failed to upload recording:", error);
    throw error;
  }
}

export async function getSessionRecordings(
  sessionId: string
): Promise<string[]> {
  try {
    const response = await fetch(
      withProfile(`${API_BASE}/api/sessions/${sessionId}/recordings`)
    );

    if (!response.ok) {
      throw new Error(`Failed to get recordings: ${response.status}`);
    }

    const result = await response.json();
    return result.recordings.map((r: any) => r.blob_url);
  } catch (error) {
    console.error("Failed to get session recordings:", error);
    return [];
  }
}

export async function deleteSessionRecordings(
  sessionId: string
): Promise<void> {
  try {
    const recordings = await getSessionRecordings(sessionId);

    for (const recording of recordings) {
      await fetch(
        withProfile(
          `${API_BASE}/api/sessions/${sessionId}/recordings/${recording}`
        ),
        {
          method: "DELETE",
        }
      );
    }
  } catch (error) {
    console.error("Failed to delete session recordings:", error);
  }
}

export async function initBlobStorage() {
  return true; // Always succeed since backend handles this
}
